import { LayoutHeader } from "@/Header";
import { createFileRoute, useSearch, useRouter } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  useBusinessCompanyMemberInfo,
  useDeleteBusinessCompanyMember,
  useUpdateBusinessCompanyMemberPermissions,
} from "~/services/business/myPage";
import { formatPhoneNumber } from "../../../utils/dataTransform";
import { Dialog } from "@/Dialog";

export const Route = createFileRoute("/business/layout/user-employee-detail")({
  component: UserSettingEmployeeDetail,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      id: Number(search.id) || 0,
    };
  },
});

function UserSettingEmployeeDetail() {
  const { id } = useSearch({ from: "/business/layout/user-employee-detail" });
  const { history } = useRouter();
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isPermissionSavedModalVisible, setIsPermissionSavedModalVisible] =
    useState(false);
  const [expandedMenuSeq, setExpandedMenuSeq] = useState<number | null>(null);
  const [editedMenus, setEditedMenus] = useState<
    Array<{
      menuSeq: number;
      menuName: string;
      permissions: Array<{
        permissionsSeq: number;
        permissionsName: string;
        hasPermissions: boolean;
      }>;
    }>
  >([]);

  const {
    data: employeeData,
    isLoading,
    error,
  } = useBusinessCompanyMemberInfo(id);

  const deleteMemberMutation = useDeleteBusinessCompanyMember();
  const updatePermissionsMutation = useUpdateBusinessCompanyMemberPermissions();

  const originalMenus = useMemo(() => employeeData?.menus ?? [], [employeeData]);
  const member = employeeData?.infoBusinessUserDto;

  useEffect(() => {
    setEditedMenus(originalMenus);
  }, [originalMenus]);

  const isDirty = useMemo(() => {
    // menus/permissions 순서가 서버와 동일하게 내려온다는 가정 하에 stringify 비교
    return JSON.stringify(editedMenus) !== JSON.stringify(originalMenus);
  }, [editedMenus, originalMenus]);

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}.${month}.${day}`;
    } catch {
      return dateString;
    }
  };

  const getUserTypeText = (cdUserType: string) => {
    switch (cdUserType) {
      case "USER_TYPE_TEACHER":
        return "선생님";
      case "USER_TYPE_OWNER":
        return "원장";
      default:
        return "임직원";
    }
  };

  const handleDeleteClick = () => {
    setIsDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const result = await deleteMemberMutation.mutateAsync({ bizUserSeq: id });

      if (result.success) {
        setIsDeleteModalVisible(false);

        // 삭제 성공 시 히스토리를 조작하여 뒤로가기 시 설정 페이지로 이동하도록 함
        // 1. 현재 페이지를 설정 페이지로 변경
        window.history.replaceState(
          null,
          "",
          "/business/layout/user-owner-setting"
        );

        // 2. 임직원 관리 페이지로 이동 (push 사용)
        history.push("/business/layout/user-employee-management");
      } else {
        console.error("임직원 삭제 실패:", result.message);
      }
    } catch (error) {
      console.error("임직원 삭제 에러:", error);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalVisible(false);
  };

  if (isLoading) {
    return (
      <>
        <LayoutHeader
          title={"임직원 정보 관리"}
          hasBackBtn
          hasFootPrint={false}
        />
        <div className="flex flex-col flex-auto bg-[#F5F5F5] justify-center items-center">
          <div className="text-sm text-gray-500">로딩 중...</div>
        </div>
      </>
    );
  }

  if (error || !employeeData) {
    return (
      <>
        <LayoutHeader
          title={"임직원 정보 관리"}
          hasBackBtn
          hasFootPrint={false}
        />
        <div className="flex flex-col flex-auto bg-[#F5F5F5] justify-center items-center">
          <div className="text-red-500">
            임직원 정보를 불러오는 중 오류가 발생했습니다.
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <LayoutHeader
        title={"임직원 정보 관리"}
        hasBackBtn
        hasFootPrint={false}
      />
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col bg-[#F5F5F5]">
        {/* 임직원 상세 정보 카드 */}
        <div className="p-3 mx-4 mt-4 bg-white rounded-lg border-[1px] border-[#E9E9E9]">
          <div className="flex items-center space-x-4">
            <div className="flex gap-4 items-center">
              {/* 프로필 이미지 */}
              <div className="w-16 h-16 bg-[#E5E5E5] rounded-full flex items-center justify-center mr-2">
                <svg
                  className="w-8 h-8 text-[#999999]"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
              </div>

              {/* 이름과 소속 */}
              <div className="flex flex-col">
                <div className="text-base font-medium text-black">
                  {member?.bizUserName ?? ""}
                </div>
                <div className="text-sm text-[#666666]">
                  {getUserTypeText(member?.cdUserType ?? "")}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 상세 정보 섹션 */}
        <div className="p-4 mx-4 mt-4 bg-white rounded-lg border-[1px] border-[#E9E9E9]">
          <div className="space-y-4">
            {/* 이메일 */}
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-[#666666]">이메일</span>
              <span className="text-sm text-black">{member?.email ?? ""}</span>
            </div>

            {/* 연락처 */}
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-[#666666]">연락처</span>
              <span className="text-sm text-black">
                {formatPhoneNumber(member?.phoneNumber ?? "")}
              </span>
            </div>

            {/* 등록일 */}
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-[#666666]">등록일</span>
              <span className="text-sm text-black">
                {formatDate(member?.dtRegist ?? "")}
              </span>
            </div>

            {/* 최종 수정일 */}
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-[#666666]">
                최종 수정일
              </span>
              <span className="text-sm text-black">
                {formatDate(member?.dtUpdate ?? "")}
              </span>
            </div>
          </div>
        </div>

        {/* 권한 설정 */}
        <div className="p-4 mx-4 mt-4 bg-white rounded-lg border-[1px] border-[#E9E9E9]">
          <div className="flex items-center justify-between mb-3">
            <div className="text-base font-semibold text-black">권한 설정</div>
            <div className="text-xs text-[#999999]">
              메뉴를 펼쳐 권한을 수정하세요
            </div>
          </div>

          {editedMenus.length === 0 ? (
            <div className="text-sm text-gray-500 py-6 text-center">
              설정 가능한 권한이 없습니다.
            </div>
          ) : (
            <div className="space-y-2">
              {editedMenus.map((menu) => {
                const isExpanded = expandedMenuSeq === menu.menuSeq;
                return (
                  <div
                    key={menu.menuSeq}
                    className="border border-[#E9E9E9] rounded-lg overflow-hidden"
                  >
                    <button
                      type="button"
                      className="w-full flex items-center justify-between px-4 py-3 bg-[#FAFAFA]"
                      onClick={() =>
                        setExpandedMenuSeq((prev) =>
                          prev === menu.menuSeq ? null : menu.menuSeq
                        )
                      }
                    >
                      <div className="text-sm font-medium text-black">
                        {menu.menuName}
                      </div>
                      <div className="text-xs text-[#666666]">
                        {isExpanded ? "접기" : "펼치기"}
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="px-4 py-2 bg-white">
                        <div className="space-y-2">
                          {menu.permissions.map((perm) => (
                            <label
                              key={perm.permissionsSeq}
                              className="flex items-center justify-between py-2"
                            >
                              <span className="text-sm text-[#333333]">
                                {perm.permissionsName}
                              </span>
                              <button
                                type="button"
                                role="checkbox"
                                aria-checked={perm.hasPermissions}
                                onClick={() => {
                                  const checked = !perm.hasPermissions;
                                  setEditedMenus((prev) =>
                                    prev.map((m) =>
                                      m.menuSeq !== menu.menuSeq
                                        ? m
                                        : {
                                            ...m,
                                            permissions: m.permissions.map((p) =>
                                              p.permissionsSeq !==
                                              perm.permissionsSeq
                                                ? p
                                                : {
                                                    ...p,
                                                    hasPermissions: checked,
                                                  }
                                            ),
                                          }
                                    )
                                  );
                                }}
                                className={[
                                  "h-5 w-5 rounded border flex items-center justify-center",
                                  "focus:outline-none focus:ring-2 focus:ring-[#FF8A00]/40",
                                  perm.hasPermissions
                                    ? "bg-[#FF8A00] border-[#FF8A00]"
                                    : "bg-white border-[#D1D5DB]",
                                ].join(" ")}
                              >
                                {perm.hasPermissions && (
                                  <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    aria-hidden="true"
                                  >
                                    <path
                                      d="M20 6L9 17L4 12"
                                      stroke="white"
                                      strokeWidth="2.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                )}
                              </button>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 권한 저장 */}
        <div className="px-4 mt-4">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setEditedMenus(originalMenus)}
              disabled={!isDirty || updatePermissionsMutation.isPending}
              className="flex-1 py-3 border border-[#E9E9E9] rounded-lg text-sm text-gray-700 bg-white disabled:opacity-50"
            >
              취소
            </button>
            <button
              type="button"
              onClick={async () => {
                try {
                  await updatePermissionsMutation.mutateAsync({
                    bizUserSeq: id,
                    permissions: editedMenus,
                  });
                  setIsPermissionSavedModalVisible(true);
                } catch (e) {
                  console.error("권한 저장 실패:", e);
                }
              }}
              disabled={!isDirty || updatePermissionsMutation.isPending}
              className="flex-1 py-3 bg-[#FF8A00] text-white rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {updatePermissionsMutation.isPending ? "저장 중..." : "저장"}
            </button>
          </div>
        </div>

        <div className="px-4 pb-4 mt-5">
          <button
            className="w-full text-sm text-gray-400 text-[#666666]"
            onClick={handleDeleteClick}
            disabled={deleteMemberMutation.isPending}
          >
            {deleteMemberMutation.isPending ? "삭제 중..." : "임직원 정보 삭제"}
          </button>
        </div>
      </div>

      {/* 삭제 확인 모달 */}
      <Dialog
        visible={isDeleteModalVisible}
        onClose={handleDeleteCancel}
        persist={false}
      >
        <div className="flex flex-col items-center text-center">
          <div className="mb-2 text-lg font-semibold text-black">
            임직원 정보 삭제
          </div>
          <div className="text-sm text-[#666666]">
            삭제된 정보는 복구할 수 없습니다.
          </div>
          <div className="text-sm text-[#666666] mb-6">
            해당 훈련사는 로그인 후 본 사업장 계정에 접근할 수 없습니다.
          </div>
          <div className="flex gap-3 w-full">
            <button
              onClick={handleDeleteCancel}
              disabled={deleteMemberMutation.isPending}
              className="flex-1 py-2 font-semibold text-black border-[1px] border-[#666666] rounded-full"
            >
              취소
            </button>
            <button
              onClick={handleDeleteConfirm}
              disabled={deleteMemberMutation.isPending}
              className="flex-1 py-2 font-semibold text-white rounded-full bg-yellow disabled:opacity-50"
            >
              확인
            </button>
          </div>
        </div>
      </Dialog>

      {/* 권한 저장 완료 모달 */}
      <Dialog
        visible={isPermissionSavedModalVisible}
        onClose={() => setIsPermissionSavedModalVisible(false)}
        persist={false}
      >
        <div className="flex flex-col items-center text-center">
          <div className="mb-2 text-lg font-semibold text-black">
            임직원 권한 정보를 수정했습니다
          </div>
          <div className="flex w-full mt-4">
            <button
              onClick={() => setIsPermissionSavedModalVisible(false)}
              className="w-full py-2 font-semibold text-white rounded-full bg-yellow"
            >
              확인
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
